/**
 * +prof: gathering in a world session (docs/design/professions.md §5–7). WorldApp owns one and
 * forwards its map loads, upkeep, sim events and interacts here:
 *
 *  - the sim knows the hero's skills (`setGatherSkill` on load and after every skill-up) and Auto's
 *    setting (Hunt / Gather / Both, the brain's `setGatherMode`);
 *  - the "[T] Gather Sunleaf" prompt for the nearest gatherable node in reach (T / clicking it /
 *    right-clicking the node starts it: sim interact `gather:<id>`);
 *  - the channel bar and its sound while the sim channels; cancels and refusals say why;
 *  - a finished gather goes to the server as `fieldGather` (it rolls the skill-up and the yield);
 *    the result pops XP, gold and materials from the node, flashes "Foraging 23 → 26", syncs the
 *    sim's skill and plays a level-up through the world's usual path;
 *  - the node tooltip under the cursor, tracking markers, and the first-node tip (once per viewer).
 */
import type { DeepReadonly, GatherEvent, GatherNodeDef, GatherProfession, SimEvent, UnitId, World } from '@keystone/sim';
import { GATHER_REACH, gatherNodeReady } from '@keystone/sim';
import { GATHER_COLOR_CSS, PROFESSION_NAME, gatherColor, gatherNodeKind, gatherTier } from '@keystone/content';
import type { GatherMode, GrindBrain } from '@keystone/bots';
import { type FieldGain, type GatherResult, professionsOf } from '@keystone/progression';
import type { Session } from '../game/session';
import type { ProfileStore } from '../meta/profileStore';
import { playSound } from '../audio/engine';
import { kmToast } from '../meta/kmmo/components';
import { rewardPops } from '../hud/field/rewardPops';
import type { RewardPart } from './rewardPopModel';
import { type NpcPrompt } from './fieldView';
import { type ProfMarker, loadProfSettings, profView, resetProfView, saveProfSettings } from './profView';
import { lineLabel, lineRarity, skillUpText } from '../hud/professions/profModel';

/** Show the first-node tip when a node comes this close. */
const TIP_YD = 22;
/** Hover / click: the cursor within this many pixels of a node's projected body, nodes this close to the hero. */
const PICK_PX = 26;
const PICK_RANGE_YD = 60;
/** Tracking marks nodes this close. */
const TRACK_YD = 120;
const TRACK_MAX = 10;
/** Right-click with more travel than this is a camera drag, not a click. */
const CLICK_SLOP = 6;

const dist = (a: { x: number; y: number }, b: { x: number; y: number }): number => Math.hypot(a.x - b.x, a.y - b.y);

const REFUSE: Readonly<Record<NonNullable<GatherEvent['reason']>, string>> = {
  moved: 'Gathering interrupted: you moved.',
  damage: 'Gathering interrupted: you were hit.',
  combat: 'You cannot gather while in combat.',
  cast: 'Gathering interrupted.',
  dead: 'You cannot gather now.',
  skill: '',
  far: 'You are too far away.',
  depleted: 'There is nothing left to gather here yet.',
  busy: 'You are already gathering.',
};

export interface GatherHost {
  store: ProfileStore;
  canvasHost: HTMLElement;
  /** Is Auto driving the hero right now (its refusals stay quiet)? */
  autoOn(): boolean;
  /** Turn Auto off (manual gathering takes control). */
  stopAuto(): void;
  /** XP / level-up / quest results of a field intent (WorldApp.applyGain). */
  applyGain(g: FieldGain): void;
}

interface Bound {
  session: Session;
  mapId: string;
  heroId: UnitId;
  nodes: readonly GatherNodeDef[];
  byId: Map<string, GatherNodeDef>;
  brain: GrindBrain | null;
  sound: ReturnType<typeof setInterval> | null;
  /** Gathers waiting on the server (one at a time; the sim can't finish two at once). */
  inFlight: number;
}

export class GatherController {
  private b: Bound | null = null;
  private tipSeen = false;
  private downAt: { x: number; y: number } | null = null;
  private lastMove = 0;
  /** Last cursor over the canvas (the tooltip is re-picked at 5 Hz as the world moves under it). */
  private cursor: { x: number; y: number } | null = null;

  constructor(private readonly host: GatherHost) {
    const s = loadProfSettings();
    profView.autoMode = s.autoMode;
    profView.tracking = s.tracking;
    this.tipSeen = s.tipSeen;
    host.canvasHost.addEventListener('pointerdown', (e) => {
      if (e.button === 2) this.downAt = { x: e.clientX, y: e.clientY };
    }, true);
    host.canvasHost.addEventListener('pointerup', (e) => this.rightClick(e), true);
    host.canvasHost.addEventListener('pointermove', (e) => this.hover(e));
    host.canvasHost.addEventListener('pointerleave', () => {
      this.cursor = null;
      this.setHover(null);
    });
  }

  /** A map was loaded (fields only; towns / dungeons have no nodes). */
  attach(session: Session, mapId: string, heroId: UnitId, nodes: readonly GatherNodeDef[] | undefined, brain: GrindBrain | null): void {
    this.detach();
    if (!nodes?.length) return;
    this.b = { session, mapId, heroId, nodes, byId: new Map(nodes.map((n) => [n.id, n])), brain, sound: null, inFlight: 0 };
    const v = profView;
    v.active = true;
    v.actions = {
      togglePanel: () => {
        v.panelOpen = !v.panelOpen;
        v.panelVersion++;
        playSound(v.panelOpen ? 'ui.open' : 'ui.close');
      },
      setAutoMode: (m) => this.setAutoMode(m),
      setTracking: (p) => this.setTracking(p),
    };
    v.markers = () => this.markers();
    this.syncSkills();
    brain?.setGatherMode?.(v.autoMode);
    session.renderer.setGatherTracking?.(v.tracking);
    v.settingsVersion++;
  }

  detach(): void {
    if (this.b?.sound) clearInterval(this.b.sound);
    this.b = null;
    resetProfView();
  }

  /** The gather prompt for the nearest node the hero can work right now (null = none). */
  prompt(world: DeepReadonly<World>): NpcPrompt | null {
    const b = this.b;
    const u = b ? world.units[b.heroId] : undefined;
    if (!b || !u?.alive || world.field?.gather?.channels[b.heroId]) return null;
    let best: GatherNodeDef | null = null;
    let bd = GATHER_REACH + 0.5;
    for (const n of b.nodes) {
      const d = dist(n.pos, u.pos);
      if (d < bd && gatherNodeReady(world, n.id)) {
        bd = d;
        best = n;
      }
    }
    if (!best) return null;
    const skill = profView.skills[best.profession];
    const name = gatherNodeKind(best.kind)?.name ?? best.kind;
    if (skill < best.req) return { npcId: `gather:${best.id}`, name, title: `Requires ${PROFESSION_NAME[best.profession]} ${best.req}`, verb: 'gather' };
    return { npcId: `gather:${best.id}`, name, title: PROFESSION_NAME[best.profession], verb: 'gather' };
  }

  /** Upkeep (5 Hz): the first-node tip; Auto's status label. */
  upkeep(world: DeepReadonly<World>): void {
    const b = this.b;
    const u = b ? world.units[b.heroId] : undefined;
    if (!b || !u) return;
    if (this.cursor && !profView.channel) this.repick();
    // The first-node tip toast was removed (user feedback 2026-10-16): nodes explain themselves via tooltip.
  }

  /** Auto is gathering right now (the badge says "Gathering"). */
  autoGathering(): boolean {
    return this.b?.brain?.grindStatus?.().state === 'gathering';
  }

  /** T / prompt click / right-click on `gather:<id>`. */
  interact(id: string): void {
    const b = this.b;
    if (!b) return;
    const nodeId = id.startsWith('gather:') ? id.slice(7) : id;
    const n = b.byId.get(nodeId);
    if (!n) return;
    if (profView.skills[n.profession] < n.req) {
      playSound('ui.error');
      return void kmToast(`Requires ${PROFESSION_NAME[n.profession]} ${n.req}`, 'error', 2200);
    }
    if (this.host.autoOn()) this.host.stopAuto();
    b.session.command({ type: 'interact', actorId: b.heroId, what: `gather:${n.id}` });
  }

  onEvents(events: readonly SimEvent[], world: DeepReadonly<World>): void {
    const b = this.b;
    if (!b) return;
    for (const e of events) {
      if (e.type !== 'gather' || e.sourceID !== b.heroId) continue;
      const n = b.byId.get(e.nodeId);
      if (!n) continue;
      const name = gatherNodeKind(n.kind)?.name ?? n.kind;
      if (e.state === 'start') {
        profView.channel = { nodeId: n.id, name, profession: n.profession, at: performance.now(), durationMs: e.durationMs ?? n.channelMs };
        profView.channelVersion++;
        this.setHover(null);
        this.startSound(n.profession);
      } else if (e.state === 'cancel' || e.state === 'done' || e.state === 'refused') {
        this.endChannel();
        if (e.state === 'done') this.report(n, world);
        else if (!this.host.autoOn() && e.reason) {
          const msg = e.reason === 'skill' ? `Requires ${PROFESSION_NAME[n.profession]} ${n.req}` : REFUSE[e.reason];
          if (msg) kmToast(msg, e.state === 'refused' ? 'error' : 'info', 1800);
          if (e.state === 'refused') playSound('ui.error');
        }
      }
    }
  }

  // -------------------------------------------------------------------------

  private startSound(prof: GatherProfession): void {
    const b = this.b!;
    if (b.sound) clearInterval(b.sound);
    const id = prof === 'mining' ? 'gather.mine' : 'gather.herb';
    playSound(id);
    b.sound = setInterval(() => playSound(id), prof === 'mining' ? 1000 : 800);
  }

  private endChannel(): void {
    if (this.b?.sound) clearInterval(this.b.sound);
    if (this.b) this.b.sound = null;
    if (profView.channel) {
      profView.channel = null;
      profView.channelVersion++;
    }
  }

  /** The sim finished a gather: ask the server to pay it. */
  private report(n: GatherNodeDef, world: DeepReadonly<World>): void {
    const b = this.b!;
    const u = world.units[b.heroId];
    if (!u) return;
    b.inFlight++;
    const mapId = b.mapId;
    void this.host.store.act('fieldGather', { mapId, nodeId: n.id, pos: { x: Math.round(u.pos.x * 100) / 100, y: Math.round(u.pos.y * 100) / 100 } }).then((r) => {
      if (this.b === b) b.inFlight--;
      if (!r.ok) {
        console.warn('[gather] not accepted:', r.reason);
        if (!this.host.autoOn()) kmToast(r.reason, 'error', 2200);
        return;
      }
      const g = r as unknown as FieldGain & { gather: GatherResult };
      this.paid(n, g);
    });
  }

  private paid(n: GatherNodeDef, g: FieldGain & { gather: GatherResult }): void {
    const res = g.gather;
    const parts: RewardPart[] = [];
    if (g.xp) parts.push({ kind: 'xp', amount: g.xp, ...(res.bonus ? { label: n.profession === 'mining' ? 'Rich vein!' : 'Lush patch!' } : {}) });
    if (g.gold) parts.push({ kind: 'gold', amount: g.gold });
    for (const l of res.lines) if (l.resource !== 'gold') parts.push({ kind: 'item', amount: l.amount, rarity: lineRarity(l), label: lineLabel(l) });
    if (parts.length) rewardPops.current?.add(n.pos, parts);
    playSound(res.lines.some((l) => l.rare) ? 'reward.rare' : 'reward.coin');
    const now = performance.now();
    if (res.skillAfter > res.skillBefore) {
      profView.toasts.push({ kind: 'skill', title: skillUpText(res.profession, res.skillBefore, res.skillAfter), text: `Your skill in ${PROFESSION_NAME[res.profession]} has increased.`, at: now });
      playSound('ui.chime');
      // Crossing a tier's requirement unlocks the next node kind.
      for (let t = 1; t <= 4; t++) {
        const req = gatherTier(t).req;
        if (res.skillBefore < req && res.skillAfter >= req) {
          const kind = [...this.b!.byId.values()].find((x) => x.profession === res.profession && x.tier === t);
          const nm = kind ? gatherNodeKind(kind.kind)?.name : undefined;
          if (nm) profView.toasts.push({ kind: 'tip', title: `New: ${nm}`, text: `You can now gather ${nm}.`, at: now });
        }
      }
    }
    for (const l of res.lines) if (l.rare) profView.toasts.push({ kind: 'rare', title: `Rare find: ${lineLabel(l)}!`, text: '', at: now });
    this.syncSkills();
    this.host.applyGain(g);
  }

  /** Profile → profView and the sim (the gating the sim and Auto use). */
  syncSkills(): void {
    const c = this.host.store.profile.character;
    const st = professionsOf(c);
    profView.skills = { ...st.skills };
    profView.gathers = st.gathers;
    profView.byKind = { ...st.byKind };
    profView.skillsVersion++;
    const b = this.b;
    if (b) b.session.command({ type: 'setGatherSkill', actorId: b.heroId, foraging: st.skills.foraging, mining: st.skills.mining });
  }

  private setAutoMode(m: GatherMode): void {
    profView.autoMode = m;
    profView.settingsVersion++;
    saveProfSettings({ autoMode: m });
    this.b?.brain?.setGatherMode?.(m);
    playSound('ui.click');
  }

  private setTracking(p: GatherProfession | null): void {
    profView.tracking = p;
    profView.settingsVersion++;
    saveProfSettings({ tracking: p });
    this.b?.session.renderer.setGatherTracking?.(p);
    playSound('ui.click');
  }

  // ---- pointer: tooltip and right-click ---------------------------------------------------------

  /** The node under a screen pixel: the cursor near its projected body (base to top), nearest on screen. */
  private nodeAt(clientX: number, clientY: number): GatherNodeDef | null {
    const b = this.b;
    if (!b) return null;
    const u = b.session.sim.getWorld().units[b.heroId];
    if (!u) return null;
    const r = b.session.renderer;
    let best: GatherNodeDef | null = null;
    let bd = PICK_PX;
    for (const n of b.nodes) {
      if (dist(n.pos, u.pos) > PICK_RANGE_YD) continue;
      const lo = r.project(n.pos, 0.1);
      const hi = r.project(n.pos, n.profession === 'mining' ? 1.3 : 0.9);
      if (!lo.visible && !hi.visible) continue;
      const dx = hi.x - lo.x;
      const dy = hi.y - lo.y;
      const l2 = dx * dx + dy * dy;
      const t = l2 > 0 ? Math.max(0, Math.min(1, ((clientX - lo.x) * dx + (clientY - lo.y) * dy) / l2)) : 0;
      const d = Math.hypot(clientX - (lo.x + dx * t), clientY - (lo.y + dy * t));
      if (d < bd) {
        bd = d;
        best = n;
      }
    }
    return best;
  }

  private hover(e: PointerEvent): void {
    if (!this.b || e.buttons !== 0) return this.setHover(null);
    const now = performance.now();
    if (now - this.lastMove < 50) return;
    this.lastMove = now;
    this.cursor = { x: e.clientX, y: e.clientY };
    this.repick();
  }

  private repick(): void {
    const c = this.cursor;
    if (!this.b || !c) return this.setHover(null);
    const e = { clientX: c.x, clientY: c.y };
    const n = this.nodeAt(e.clientX, e.clientY);
    if (!n) return this.setHover(null);
    const w = this.b.session.sim.getWorld();
    const st = w.field?.gather?.nodes[n.id];
    const u = w.units[this.b.heroId];
    profView.hover = { x: e.clientX, y: e.clientY, nodeId: n.id, backInMs: st && w.time < st.readyAt ? st.readyAt - w.time : 0, dist: u ? dist(u.pos, n.pos) : 99 };
    profView.hoverVersion++;
  }

  private setHover(h: null): void {
    if (profView.hover === h) return;
    profView.hover = h;
    profView.hoverVersion++;
  }

  private rightClick(e: PointerEvent): void {
    const d = this.downAt;
    this.downAt = null;
    if (e.button !== 2 || !d || Math.hypot(e.clientX - d.x, e.clientY - d.y) > CLICK_SLOP || !this.b) return;
    const n = this.nodeAt(e.clientX, e.clientY);
    if (!n) return;
    const u = this.b.session.sim.getWorld().units[this.b.heroId];
    if (u && dist(u.pos, n.pos) > GATHER_REACH) return void kmToast(`Walk closer to the ${gatherNodeKind(n.kind)?.name ?? 'node'}.`, 'info', 1600);
    e.stopPropagation();
    this.interact(n.id);
  }

  // ---- tracking ---------------------------------------------------------------------------------

  private markers(): ProfMarker[] {
    const b = this.b;
    const prof = profView.tracking;
    if (!b || !prof) return [];
    const w = b.session.sim.getWorld();
    const u = w.units[b.heroId];
    if (!u) return [];
    const near = b.nodes
      .filter((n) => n.profession === prof && gatherNodeReady(w, n.id))
      .map((n) => ({ n, d: dist(n.pos, u.pos) }))
      .filter((x) => x.d < TRACK_YD && x.d > 3)
      .sort((a, c) => a.d - c.d)
      .slice(0, TRACK_MAX);
    const W = window.innerWidth;
    const H = window.innerHeight;
    const out: ProfMarker[] = [];
    for (const { n, d } of near) {
      const p = b.session.renderer.project(n.pos, 2.4);
      const color = GATHER_COLOR_CSS[gatherColor(profView.skills[prof], n.req)];
      const name = gatherNodeKind(n.kind)?.name ?? n.kind;
      if (p.visible && p.x > 20 && p.x < W - 20 && p.y > 20 && p.y < H - 20) {
        out.push({ x: p.x, y: p.y, edge: false, angle: 0, color, name, dist: d });
        continue;
      }
      // Off screen (or behind the camera): pin to the edge in the node's direction from the centre.
      let dx = p.x - W / 2;
      let dy = p.y - H / 2;
      if (!p.visible) {
        dx = -dx;
        dy = -dy;
      }
      const k = Math.min((W / 2 - 28) / Math.max(1e-3, Math.abs(dx)), (H / 2 - 28) / Math.max(1e-3, Math.abs(dy)));
      out.push({ x: W / 2 + dx * k, y: H / 2 + dy * k, edge: true, angle: Math.atan2(dy, dx), color, name, dist: d });
    }
    return out;
  }
}
